import DOMPurify from 'dompurify'

const OPTION_FORM_TYPES = new Set([
    "dropdownField", "dropdownForm", "selectListForm",
    "checkboxField", "checkboxForm",
    "multipleChoiceField", "multipleChoiceForm",
    "radioField", "radioForm",
])

const optionColumnTypes = new Set(["select", "dropdown", "radio", "checkbox", "mcq"])

const hasOptionSet = (widget) =>
    OPTION_FORM_TYPES.has(widget?.formType) || optionColumnTypes.has(widget?.inputType)

const isFilled = (value) => value !== undefined && value !== null && value !== ""

const flattenWidgets = (items = []) => {
    const widgets = []
    ;(Array.isArray(items) ? items : []).forEach((item) => {
        if (!item) return
        widgets.push(item)
        if (Array.isArray(item.children) && item.formType !== "tableForm") {
            widgets.push(...flattenWidgets(item.children))
        }
        if (item.formType === "tableForm" && Array.isArray(item.columns)) {
            item.columns.forEach((column) => {
                let formType = column.formType
                if (column.inputType === "select" || column.inputType === "dropdown") formType = "dropdownField"
                else if (column.inputType === "checkbox") formType = "checkboxField"
                else if (column.inputType === "radio") formType = "radioField"
                else if (column.inputType === "mcq") formType = "multipleChoiceField"
                else if (column.inputType === "textarea" || column.inputType === "textAreaForm") formType = "textAreaForm"
                widgets.push({
                    ...column,
                    formType: formType || column.formType,
                    id: item.id + "::" + column.id,
                    parentTableId: item.id,
                })
            })
        }
    })
    return widgets
}

const normalizeMention = (value) => String(value ?? "").trim().toLowerCase()

const mentionMatchesWidget = (mentionId, widget) => {
    if (!mentionId || !widget) return false
    const mention = normalizeMention(mentionId)
    const exact = [widget.id, widget.label, widget.actualLabel]
        .filter((value) => value != null && value !== "")
        .map(normalizeMention)
    if (exact.includes(mention)) return true
    // Table columns from getAllWidgets are labeled "Address (TableName)"
    const label = normalizeMention(widget.label)
    return Boolean(mention) && label.startsWith(`${mention} (`)
}

const findWidgetForMention = (formItems, mentionId) => {
    if (mentionId == null || mentionId === "") return null
    const mentionKey = String(mentionId)
    const widgets = flattenWidgets(formItems)
    return widgets.find((item) => String(item?.id) === mentionKey)
        || widgets.find((item) => String(item?.id || "").endsWith("::" + mentionKey))
        || widgets.find((item) => mentionMatchesWidget(mentionKey, item) && normalizeMention(item?.actualLabel) === normalizeMention(mentionKey))
        || widgets.find((item) => mentionMatchesWidget(mentionKey, item) && normalizeMention(item?.label) === normalizeMention(mentionKey))
        || widgets.find((item) => mentionMatchesWidget(mentionKey, item))
        || null
}

const valueForMention = (data, mentionId, widget) => {
    if (!data || typeof data !== "object") return undefined
    if (isFilled(data[mentionId])) return data[mentionId]
    if (widget?.id != null && isFilled(data[widget.id])) return data[widget.id]
    return valueFromTableRows(data, widget, mentionId)
}

const valuesFromRows = (rows, columnId) => {
    if (!Array.isArray(rows) || columnId == null || columnId === "") return []
    return rows
        .map((row) => (row && typeof row === "object" && !Array.isArray(row) ? row[columnId] : undefined))
        .filter(isFilled)
}

const valueFromTableRows = (data, widget, mentionId) => {
    if (!data || typeof data !== "object") return undefined

    const widgetId = widget?.id != null ? String(widget.id) : ""
    let tableId = widget?.parentTableId
    let columnId = mentionId
    if (widgetId.includes("::")) {
        const [tid, cid] = widgetId.split("::")
        tableId = tableId || tid
        columnId = cid || columnId
    }

    if (tableId) {
        const hits = valuesFromRows(data[tableId], columnId)
        if (hits.length === 1) return hits[0]
        if (hits.length > 1) return hits
    }

    for (const value of Object.values(data)) {
        const hits = valuesFromRows(value, mentionId)
        if (hits.length === 1) return hits[0]
        if (hits.length > 1) return hits
    }
    return undefined
}

const labelForOptionValue = (widget, rawValue) => {
    const options = Array.isArray(widget?.options) ? widget.options : []
    const toLabel = (value) => {
        const match = options.find((opt) =>
            String(opt?.value) === String(value) || String(opt?.label) === String(value)
        )
        return match?.label ?? value
    }
    if (Array.isArray(rawValue)) {
        return rawValue.map(toLabel).filter((value) => value != null && value !== "").join(", ")
    }
    return toLabel(rawValue)
}

const displayValue = (value) => {
    if (!isFilled(value)) return ""
    if (Array.isArray(value)) {
        return value.filter((part) => part != null && part !== "").join(", ")
    }
    return value
}

const injectDataInTemplate = (content, data, formItems) => {
    const parser = new DOMParser();
    let parsedDocument = parser.parseFromString(content, 'text/html');
    let allWidgets = parsedDocument.querySelectorAll(`[data-lexical-beautiful-mention-value], [data-lexical-beautiful-mention-data]`)

    for (const elm of allWidgets) {
        const mentionText = elm.innerText ?? elm.textContent ?? ""
        let widget = mentionText.startsWith("@") ? mentionText.substring(1) : mentionText
        let currentWidgetValue = elm.getAttribute("data-beautiful-mention")
        const mentionDataAttr = elm.getAttribute("data-lexical-beautiful-mention-data");
        let mentionId = null
        if (mentionDataAttr) {
            try {
                mentionId = JSON.parse(mentionDataAttr).id
            } catch (error) {
                mentionId = null
            }
        }
        if (widget.includes("]") && currentWidgetValue && currentWidgetValue.includes("@")) {
            mentionId = currentWidgetValue.replace("@", "")
        } else if (!mentionId && currentWidgetValue) {
            mentionId = currentWidgetValue.replace(/^@/, "")
        }

        const mentionedWidget = findWidgetForMention(formItems, mentionId)
        const rawValue = valueForMention(data, mentionId, mentionedWidget)

        if (mentionedWidget && hasOptionSet(mentionedWidget) && isFilled(rawValue)) {
            elm.innerHTML = DOMPurify.sanitize(String(labelForOptionValue(mentionedWidget, rawValue) ?? ""))
            continue
        }

        if (mentionId && isFilled(rawValue)) {
            elm.innerHTML = DOMPurify.sanitize(displayValue(rawValue))
        } else {
            elm.textContent = " "
        }
    }
    return parsedDocument.documentElement.innerHTML
}
export default injectDataInTemplate
